import {
  ensureFreshSessionAction,
  refreshSessionAction,
  signOutAction,
} from "@/actions/auth-action";
import { ApiConstant, AppConstant, EnvConstant, PathConstant } from "@/const";
import { IDataPagingResponseModel, IDataResponseModel, ISession } from "@/models";
import { ApiUtils, CommonUtils } from "@/utils";
import { createUrlWithParams, isClient } from "@/utils/api.utils";
import { redirect } from "next/navigation";
import cookie from "js-cookie";
import { isRedirectError } from "next/dist/client/components/redirect";
import { COOKIE_STORAGE } from "@/const/app.const";
type CustomOptions = Omit<RequestInit, "method" | "signal"> & {
  baseUrl?: string | undefined;
  timeout?: number;
  params?: IParams;
  controller?: AbortController;
  accessToken?: string;
  skipAuth?: boolean;
  isHideMessage?: boolean;
  skipLoading?: boolean;
  _retry?: boolean;
};
export interface IParams {
  [key: string]: string | number | boolean | undefined | any;
}

// type EntityErrorPayload = {
//   message: string
//   errors: {
//     field: string
//     message: string
//   }[]
// }
let requestCount = 0;
let clientRefreshPromise: Promise<ISession | null> | null = null;
const SESSION_REFRESHED_EVENT = "auth:session-refreshed";

const refreshClientSession = async () => {
  if (!clientRefreshPromise) {
    clientRefreshPromise = refreshSessionAction().finally(() => {
      clientRefreshPromise = null;
    });
  }

  return await clientRefreshPromise;
};

const showLoading = () => {
  if (isClient()) {
    if (requestCount === 0) {
      CommonUtils.toggleAppProgress(true);
    }
    requestCount++;
  }
};

const hideLoading = () => {
  if (isClient()) {
    requestCount--;
    if (requestCount === 0) {
      CommonUtils.toggleAppProgress(false);
    }
  }
};
export interface IErrorMessage {
  message: string;
  [key: string]: any;
}
export class HttpError extends Error {
  status: number;
  payload: IErrorMessage;
  constructor({ status, payload }: { status: number; payload: any }) {
    super("Http Error");
    this.status = status;
    this.payload = payload;
  }
}
export class BadRequestError extends HttpError {
  status: 400;
  payload: { message: string };
  constructor({
    status,
    payload,
  }: {
    status: 400;
    payload: { message: string };
  }) {
    super({ status, payload });
    this.status = status;
    this.payload = payload;
  }
}
// export class EntityError extends HttpError {
//   status: 422
//   payload: EntityErrorPayload
//   constructor({
//     status,
//     payload
//   }: {
//     status: 422
//     payload: EntityErrorPayload
//   }) {
//     super({ status, payload })
//     this.status = status
//     this.payload = payload
//   }
// }

const request = async <T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  url: string,
  options?: CustomOptions | undefined,
) => {
  const isShowMessage = !options?.isHideMessage;
  const skipLoading = Boolean(options?.skipLoading);
  try {
    if (!skipLoading) {
      showLoading();
    }
    let controller = options?.controller;
    if (!controller) {
      controller = new AbortController();
    }

    let timeout = options?.timeout;
    if (timeout === undefined) {
      timeout = ApiConstant.TIMEOUT;
    }

    let body = options?.body;
    if (body && !(body instanceof FormData)) {
      body = JSON.stringify(body);
    }

    const baseHeaders: {
      [key: string]: string;
    } = ApiConstant.HEADER_DEFAULT;
    if (body instanceof FormData) {
      delete baseHeaders["Content-Type"];
    } else {
      baseHeaders["Content-Type"] = "application/json";
    }
    let accessToken = null;

    let session: ISession | null = null;
    if (options?.skipAuth) {
      accessToken = null;
    } else if (options?.accessToken !== undefined) {
      accessToken = options?.accessToken;
    } else if (isClient()) {
      accessToken = cookie.get(COOKIE_STORAGE.AccessToken);
    } else {
      session = await ensureFreshSessionAction();
      if (session && session.accessToken) {
        accessToken = session.accessToken;
      }
    }
    // add host domain
    // baseHeaders.HostRequest = domain;
    // add đơn vị
    if (accessToken) {
      baseHeaders.authorization = "Bearer " + accessToken;
    }
    // Nếu không truyền baseUrl (hoặc baseUrl = undefined) thì lấy từ envConfig
    // Nếu truyền baseUrl thì lấy giá trị truyền vào, truyền vào '' thì đồng nghĩa với việc chúng ta gọi API đến Next.js Server

    const baseUrl =
      options?.baseUrl === undefined
        ? EnvConstant.NEXT_PUBLIC_MAIN_API_URL
        : options.baseUrl;

    let fullUrl = url.startsWith("/")
      ? `${baseUrl}${url}`
      : `${baseUrl}/${url}`;
    if (options?.params !== undefined) {
      fullUrl = createUrlWithParams(fullUrl, options.params);
    }
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    const res = await fetch(fullUrl, {
      // cache: "no-store",
      signal: controller.signal,
      ...options,
      headers: {
        ...baseHeaders,
        ...options?.headers,
      },
      body,
      method,
    });
    clearTimeout(timeoutId);
    let payload = null;
    const contentType = res.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      payload = await res.json();
    } else {
      payload = await res.text();
    }
    const data = {
      status: res.status,
      payload,
    };
    // xử lý request và response trước khi trả về cho phía component
    if (!res.ok) {
      EnvConstant.IS_DEV && console.log(payload);
      if (options?.skipAuth && res.status === ApiConstant.STT_UNAUTHORIZED) {
        if (!skipLoading) {
          hideLoading();
        }
        return data;
      }
      if (res.status === ApiConstant.STT_BAD_REQUEST) {
        if (isShowMessage) {
          ApiUtils.showMessageClient({
            message: (payload as IErrorMessage).message || "Đã có lỗi xảy ra!",
            variant: AppConstant.ERROR_VARIANT,
          });
        }
      } else if (res.status === ApiConstant.STT_FORBIDDEN) {
        if (isShowMessage) {
          ApiUtils.showMessageClient({
            message:
              (payload as IErrorMessage).message ||
              "Tài khoản của bạn không được cấp quyền!",
            variant: AppConstant.ERROR_VARIANT,
          });
        }
      } else if (res.status === ApiConstant.STT_UNAUTHORIZED) {
        if (isClient()) {
          if (!options?._retry) {
            const nextSession = await refreshClientSession();
            if (nextSession?.accessToken) {
              window.dispatchEvent(
                new CustomEvent<ISession>(SESSION_REFRESHED_EVENT, {
                  detail: nextSession,
                }),
              );
              if (!skipLoading) {
                hideLoading();
              }
              return await request<T>(method, url, {
                ...options,
                accessToken: nextSession.accessToken,
                _retry: true,
              });
            }
          }
          await signOutAction();
          window.location.href = PathConstant.LOGIN;
        } else {
          if (!options?._retry) {
            const nextSession = await refreshSessionAction();
            if (nextSession?.accessToken) {
              if (!skipLoading) {
                hideLoading();
              }
              return await request<T>(method, url, {
                ...options,
                accessToken: nextSession.accessToken,
                _retry: true,
              });
            }
          }
          redirect("/logout");
        }
      } else {
        if (isShowMessage) {
          ApiUtils.showMessageClient({
            message: (payload as IErrorMessage).message || "Đã có lỗi xảy ra!",
            variant: AppConstant.ERROR_VARIANT,
          });
        }
      }
    }
    if (!skipLoading) {
      hideLoading();
    }
    return data;
  } catch (errors) {
    if (!skipLoading) {
      hideLoading();
    }
    EnvConstant.IS_DEV && console.log(errors);
    if (isRedirectError(errors)) {
      throw errors;
    }
    if (
      errors instanceof Object &&
      (errors as any)?.["name"] === "AbortError"
    ) {
    } else {
      if (isShowMessage) {
        ApiUtils.showMessageClient({
          message: "Đã có lỗi xảy ra!",
          variant: AppConstant.ERROR_VARIANT,
        });
      }
    }
    return {
      status: ApiConstant.STT_INTERNAL_SERVER,
      payload: errors,
    };
  }
};

export { SESSION_REFRESHED_EVENT };

const APIMain = {
  async get<T>(
    url: string,
    params?: IParams,
    options?: Omit<CustomOptions, "body" | "params"> | undefined,
  ): Promise<IDataResponseModel<T>> {
    const res = await request<IDataResponseModel<T>>("GET", url, {
      params,
      ...options,
    });
    return res;
  },
  async getPaging<T>(
    url: string,
    params?: IParams,
    options?: Omit<CustomOptions, "body" | "params"> | undefined,
  ): Promise<IDataPagingResponseModel<T>> {
    const res = await request<IDataPagingResponseModel<T>>("GET", url, {
      params,
      ...options,
    });
    return res;
  },
  async post<T>(
    url: string,
    body?: any,
    options?: Omit<CustomOptions, "body"> | undefined,
  ): Promise<IDataResponseModel<T>> {
    const res = await request<IDataResponseModel<T>>("POST", url, {
      ...options,
      body,
    });
    return res;
  },
  async put<T>(
    url: string,
    body: any,
    options?: Omit<CustomOptions, "body"> | undefined,
  ): Promise<IDataResponseModel<T>> {
    const res = await request<IDataResponseModel<T>>("PUT", url, {
      ...options,
      body,
    });
    return res;
  },
  async patch<T>(
    url: string,
    body?: any,
    options?: Omit<CustomOptions, "body"> | undefined,
  ): Promise<IDataResponseModel<T>> {
    const res = await request<IDataResponseModel<T>>("PATCH", url, {
      ...options,
      body,
    });
    return res;
  },
  async delete<T>(
    url: string,
    body?: any,
    params?: IParams,
    options?: Omit<CustomOptions, "body" | "params"> | undefined,
  ): Promise<IDataResponseModel<T>> {
    const res = await request<IDataResponseModel<T>>("DELETE", url, {
      params,
      body,
      ...options,
    });
    return res;
  },
};

export default APIMain;
